// Shared blog data — used by /blog (list) and /blog/:slug (detail)

export const CATEGORIES = [
    { id: "all", label: "All" },
    { id: "operating", label: "Operating" },
    { id: "ai", label: "AI & Intelligence" },
    { id: "leadership", label: "Leadership" },
    { id: "engineering", label: "Engineering" },
    { id: "product", label: "Product Updates" },
];

export const CAT_COLORS = {
    operating: {
        accent: "text-cyan-300",
        chip: "bg-cyan-300/10 border-cyan-300/25",
        ring: "from-cyan-400/30 to-cyan-400/0",
    },
    ai: {
        accent: "text-electric",
        chip: "bg-electric/10 border-electric/30",
        ring: "from-electric/30 to-electric/0",
    },
    leadership: {
        accent: "text-amber-glow",
        chip: "bg-amber-300/10 border-amber-300/25",
        ring: "from-amber-400/30 to-amber-400/0",
    },
    engineering: {
        accent: "text-emerald-300",
        chip: "bg-emerald-300/10 border-emerald-300/25",
        ring: "from-emerald-400/30 to-emerald-400/0",
    },
    product: {
        accent: "text-violet-300",
        chip: "bg-violet-300/10 border-violet-300/25",
        ring: "from-violet-400/30 to-violet-400/0",
    },
};

/* Long-form body is structured as blocks for typed rendering. */
const sampleBody = ({ titleH2A, titleH2B, codeLang = "bash" }) => [
    { type: "lede", text: "There is a moment, somewhere between the fifteenth team standup and the third spreadsheet of the day, when most operators realise: this isn't a software problem, it's a coordination problem." },
    { type: "p", text: "Every fast-growing company we've worked with starts the same way. A friendly tool here, a sharper one there, a chat thread named #important-stuff. By the time the team crosses thirty people, the company runs on coincidence and the founders' memory." },
    { type: "h2", text: titleH2A },
    { type: "p", text: "We rebuilt that experience as a single graph. Sales records know which Projects pay for them. Projects know which People deliver them. People know which Decisions affect their goals. Everything resolves to one identifier — no integration syncs, no flapping records." },
    { type: "callout", tone: "tip", title: "What this changes in practice", text: "Instead of asking three teams the same question, leaders open one screen. Instead of writing a brief, Co-pilot drafts one with citations. Instead of chasing reports, reports chase you." },
    { type: "list", items: [
        "Pipeline forecast that updates itself when delivery slips",
        "Onboarding workflows that provision tools without a ticket",
        "Decisions that surface on every record they affect",
        "AI answers that cite the exact records they came from",
    ] },
    { type: "h2", text: titleH2B },
    { type: "p", text: "We built the migration importers first. Before any new feature shipped to GA, we asked: can a real team move 6 months of issues, deals and docs in under an hour? If the answer was no, the feature wasn't ready." },
    { type: "code", lang: codeLang, code: `# A real example from production:
ax migrate from jira \\
  --workspace acme \\
  --types issues,epics,sprints \\
  --range last-180d
# 14,392 records moved · 0 dropped · 2m 14s` },
    { type: "quote", text: "We replaced eleven tools and a recurring Monday meeting with one screen. The business runs the same — except now I can actually see it.", who: "Rohan Iyer · COO, Northpeak IT Services" },
    { type: "h2", text: "Where this is heading" },
    { type: "p", text: "The next 12 months are not about more features. They are about deeper reasoning across the same six pillars — the Co-pilot graduating from answering questions to drafting the next move and quietly cleaning up what got missed." },
    { type: "p", text: "If you'd like to operate at this layer, we have a free tier for teams under 8. Above that we charge per company, not per seat — because the system gets cheaper to use, not more expensive, as you grow." },
];

export const POSTS = [
    {
        id: "featured",
        slug: "operating-layer-playbook",
        category: "operating",
        title: "How fast-growing IT companies replaced 12 tools with one operating layer",
        excerpt:
            "Most companies don't have a software problem — they have a coordination problem. Here's the operating playbook used by 1,200+ teams that moved from fragmented stacks to a single intelligence layer.",
        author: "Aanya Mehta",
        role: "Head of Operations",
        bio: "Aanya runs operations at ApexBrain and previously led ops at two fast-scaling IT services firms. She writes about how systems beat heroics.",
        avatar: "A",
        avatarBg: "from-cyan-300 to-emerald-400",
        date: "Feb 14, 2026",
        read: "9 min",
        tag: "Editor's note",
        featured: true,
        tags: ["operating", "scaling", "playbook"],
        body: sampleBody({
            titleH2A: "From twelve tools to one graph",
            titleH2B: "The migration is the moment",
            codeLang: "bash",
        }),
    },
    {
        id: "ai-copilot",
        slug: "ai-copilot-reasons-across-modules",
        category: "ai",
        title: "Beyond chat: what an AI co-pilot that reasons across modules actually does",
        excerpt:
            "We rebuilt the ApexBrain Co-pilot to cite every claim back to the source record. Here's the architecture, the trade-offs, and the early production wins.",
        author: "Rohit Krishnan",
        role: "Founding Engineer",
        bio: "Rohit is a founding engineer at ApexBrain. Before that, he built ML platforms at two large fintechs.",
        avatar: "R",
        avatarBg: "from-electric to-cyan-300",
        date: "Feb 11, 2026",
        read: "12 min",
        tag: "Deep dive",
        size: "lg",
        tags: ["ai", "architecture", "co-pilot"],
        body: sampleBody({
            titleH2A: "Reasoning over the graph",
            titleH2B: "Citations are not a feature — they are the contract",
            codeLang: "python",
        }),
    },
    {
        id: "leadership-monday",
        slug: "five-numbers-monday-review",
        category: "leadership",
        title: "Five numbers your Monday review should never start without",
        excerpt:
            "Revenue Velocity, People Health, Operational Throughput, Customer Pulse, Cash Runway — what each one really means and why most dashboards get them wrong.",
        author: "Vikram Sethi",
        role: "Founder, ApexBrain",
        bio: "Vikram is the founder of ApexBrain. He writes about leadership cadences, focus and what compounds.",
        avatar: "V",
        avatarBg: "from-amber-glow to-rose-300",
        date: "Feb 7, 2026",
        read: "6 min",
        tag: "Playbook",
        tags: ["leadership", "metrics", "cadence"],
        body: sampleBody({
            titleH2A: "Why most dashboards lie",
            titleH2B: "What to do on Monday morning",
            codeLang: "bash",
        }),
    },
    {
        id: "month-end",
        slug: "month-end-close-3-days",
        category: "operating",
        title: "Closing the books in 3 days, not 12 — the workflow we shipped this quarter",
        excerpt:
            "Month-end close is where chaos hides. We re-engineered it as one continuous workflow with three approval gates. The result was unexpectedly nice.",
        author: "Priya Iyer",
        role: "Operations Lead",
        bio: "Priya leads the Operations module at ApexBrain. Recovering CFO. Believes spreadsheets should be illegal.",
        avatar: "P",
        avatarBg: "from-cyan-300 to-electric",
        date: "Feb 4, 2026",
        read: "7 min",
        tag: "From the trenches",
        tags: ["operating", "finance", "workflow"],
        body: sampleBody({
            titleH2A: "The 12-day close is a coordination tax",
            titleH2B: "Three gates, zero spreadsheets",
            codeLang: "bash",
        }),
    },
    {
        id: "release-notes",
        slug: "apexbrain-2-4-release",
        category: "product",
        title: "ApexBrain 2.4 — AI Co-pilot now GA, new compliance posture, faster Projects",
        excerpt:
            "What shipped this week: cross-module AI citations, HIPAA mode for regulated teams, and a 38% perf improvement on the Projects timeline view.",
        author: "ApexBrain Team",
        role: "Product",
        bio: "Notes from the ApexBrain product team — what shipped, why and what's next.",
        avatar: "AB",
        avatarBg: "from-violet-300 to-electric",
        date: "Feb 1, 2026",
        read: "4 min",
        tag: "What's new",
        size: "lg",
        tags: ["product-updates", "release"],
        body: sampleBody({
            titleH2A: "AI Co-pilot, generally available",
            titleH2B: "HIPAA mode, on-prem and faster timelines",
            codeLang: "bash",
        }),
    },
    {
        id: "engineering-graph",
        slug: "single-graph-six-modules",
        category: "engineering",
        title: "The single graph that lets six modules share one source of truth",
        excerpt:
            "How we modelled Sales, Operations, HR, Projects, Communication and Intelligence as one connected graph — and the lessons we learned along the way.",
        author: "Sahil Verma",
        role: "Principal Engineer",
        bio: "Sahil leads platform engineering at ApexBrain. Previously at a search infra company. Writes about data modelling and observability.",
        avatar: "S",
        avatarBg: "from-emerald-300 to-cyan-300",
        date: "Jan 27, 2026",
        read: "14 min",
        tag: "Architecture",
        tags: ["engineering", "architecture", "graph"],
        body: sampleBody({
            titleH2A: "Why a graph (and not a relational join)",
            titleH2B: "The trade-offs we wish someone had told us",
            codeLang: "typescript",
        }),
    },
    {
        id: "founder-trap",
        slug: "founder-dependency-trap",
        category: "leadership",
        title: "The founder dependency trap (and how to systematise yourself out of it)",
        excerpt:
            "Every escalation reaches the founder. Every decision needs leadership. Sound familiar? Here's the four-step pattern teams used to break out.",
        author: "Vikram Sethi",
        role: "Founder, ApexBrain",
        bio: "Vikram is the founder of ApexBrain. He writes about leadership cadences, focus and what compounds.",
        avatar: "V",
        avatarBg: "from-amber-glow to-rose-300",
        date: "Jan 22, 2026",
        read: "8 min",
        tag: "Operating",
        tags: ["leadership", "founders", "ops"],
        body: sampleBody({
            titleH2A: "How the trap closes",
            titleH2B: "The four moves out",
            codeLang: "bash",
        }),
    },
    {
        id: "ai-citations",
        slug: "ai-citations-design",
        category: "ai",
        title: "Why every AI answer in ApexBrain ships with citations",
        excerpt:
            "An AI that hallucinates inside an operating system is a liability. We built a citation layer that makes the co-pilot auditable by construction.",
        author: "Rohit Krishnan",
        role: "Founding Engineer",
        bio: "Rohit is a founding engineer at ApexBrain. Before that, he built ML platforms at two large fintechs.",
        avatar: "R",
        avatarBg: "from-electric to-cyan-300",
        date: "Jan 18, 2026",
        read: "10 min",
        tag: "Engineering",
        tags: ["ai", "citations", "engineering"],
        body: sampleBody({
            titleH2A: "Hallucinations are a non-starter",
            titleH2B: "The citation contract, in code",
            codeLang: "python",
        }),
    },
    {
        id: "okr-cycles",
        slug: "okr-cycles-without-decks",
        category: "operating",
        title: "Running OKR cycles your team actually feels — without slide decks",
        excerpt:
            "OKRs end up as a deck no one reads because they're disconnected from the work. We tied every KR back to the underlying records — here's what changed.",
        author: "Aanya Mehta",
        role: "Head of Operations",
        bio: "Aanya runs operations at ApexBrain and previously led ops at two fast-scaling IT services firms.",
        avatar: "A",
        avatarBg: "from-cyan-300 to-emerald-400",
        date: "Jan 14, 2026",
        read: "9 min",
        tag: "Playbook",
        tags: ["operating", "okrs", "playbook"],
        body: sampleBody({
            titleH2A: "Why decks are the wrong artifact",
            titleH2B: "The cadence we landed on",
            codeLang: "bash",
        }),
    },
];

export const TRENDING = [
    "AI Co-pilot citations",
    "Closing books in 3 days",
    "5 founder signals",
    "Single graph architecture",
    "Founder dependency",
];

export function findPostBySlug(slug) {
    return POSTS.find((p) => p.slug === slug);
}

export function nextPostAfter(slug) {
    const idx = POSTS.findIndex((p) => p.slug === slug);
    if (idx < 0) return null;
    return POSTS[(idx + 1) % POSTS.length];
}

export function relatedPosts(slug, n = 3) {
    const me = findPostBySlug(slug);
    if (!me) return [];
    const others = POSTS.filter((p) => p.slug !== slug);
    const sameCat = others.filter((p) => p.category === me.category);
    const rest = others.filter((p) => p.category !== me.category);
    return [...sameCat, ...rest].slice(0, n);
}
